import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Shield,
  Check,
  X,
  Zap,
  Crown,
  ArrowRight,
  Star
} from 'lucide-react'
import { useSubscription } from '../contexts/SubscriptionContext'
import { useAuth } from '../contexts/AuthContext'
import Footer from '../components/Footer'

const PricingScreen = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isPremium, mockSubscribe, subscription } = useSubscription()
  const [billingCycle, setBillingCycle] = useState('yearly')
  const [processing, setProcessing] = useState(false)

  const plans = {
    free: {
      name: 'Free',
      price: 0,
      description: 'Get started with survival basics',
      features: [
        { name: '1 Disaster Path (Nuclear Attack)', included: true },
        { name: '50 Interactive Lessons', included: true },
        { name: 'Basic Progress Tracking', included: true },
        { name: 'Emergency Mode', included: true },
        { name: 'Personality Quiz', included: true },
        { name: '3 Chaos Mode Alerts/Day', included: true },
        { name: 'All 5 Disaster Paths', included: false },
        { name: 'AI-Powered Search', included: false },
        { name: 'Family Sync & Competitions', included: false },
        { name: 'Offline Mode', included: false },
        { name: 'Completion Certificates', included: false },
        { name: 'Ad-Free Experience', included: false },
      ]
    },
    premium: {
      name: 'Premium',
      monthlyPrice: 4.99,
      yearlyPrice: 39.99,
      description: 'Complete survival mastery',
      badge: 'BEST VALUE',
      features: [
        { name: 'All 5 Disaster Paths', included: true },
        { name: '250+ Interactive Lessons', included: true },
        { name: 'Advanced Progress Analytics', included: true },
        { name: 'Emergency Mode', included: true },
        { name: 'Personality Quiz', included: true },
        { name: 'Unlimited Chaos Mode', included: true },
        { name: 'AI-Powered Search', included: true, highlight: true },
        { name: 'Family Sync & Competitions', included: true, highlight: true },
        { name: 'Offline Mode', included: true, highlight: true },
        { name: 'Completion Certificates', included: true },
        { name: 'Ad-Free Experience', included: true },
        { name: 'Printable Checklists', included: true },
      ]
    }
  }

  const handleSubscribe = async (plan = billingCycle) => {
    if (!user) {
      navigate('/auth')
      return
    }

    setProcessing(true)
    try {
      const priceIds = {
        monthly: 'price_1SW1mXKbdCpYO5hMLK8C1dok',
        yearly: 'price_1SW1nZKbdCpYO5hM8yYiLZj3',
        lifetime: 'price_1SW1plKbdCpYO5hMORwMm4nc'
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://surviveitapp.com/api'}/v1/stripe/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          priceId: priceIds[plan],
          userId: user.id,
          userEmail: user.email,
          plan
        })
      })

      const data = await response.json()

      if (data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url
      } else {
        console.error('No checkout URL returned:', data)
      }
    } catch (error) {
      console.error('Subscription error:', error)
    } finally {
      setProcessing(false)
    }
  }

  const yearlyDiscount = Math.round((1 - (plans.premium.yearlyPrice / (plans.premium.monthlyPrice * 12))) * 100)

  return (
    <div className="pricing-screen">
      {/* Header */}
      <nav className="nav">
        <div className="container">
          <div className="nav-content">
            <div className="logo" onClick={() => navigate('/')}>
              <Shield className="w-8 h-8 text-orange-500" />
              <span className="font-bold text-xl">SurviveIt</span>
            </div>
            <button
              onClick={() => navigate(user ? '/dashboard' : '/auth')}
              className="btn btn-ghost"
            >
              {user ? 'Dashboard' : 'Sign In'}
            </button>
          </div>
        </div>
      </nav>

      <main className="pricing-main">
        <div className="container">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="pricing-hero"
          >
            <h1 className="pricing-title">
              Choose Your <span className="text-gradient">Survival Plan</span>
            </h1>
            <p className="pricing-subtitle">
              Start free, upgrade when you're ready for complete preparedness
            </p>
          </motion.div>

          {/* Billing Toggle */}
          <div className="billing-toggle">
            <button
              className={`toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={`toggle-btn ${billingCycle === 'yearly' ? 'active' : ''}`}
              onClick={() => setBillingCycle('yearly')}
            >
              Yearly
              <span className="discount-badge">Save {yearlyDiscount}%</span>
            </button>
          </div>

          {/* Pricing Cards */}
          <div className="pricing-grid">
            {/* Free Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="pricing-card"
            >
              <div className="card-header">
                <h3 className="plan-name">{plans.free.name}</h3>
                <div className="plan-price">
                  <span className="price">$0</span>
                  <span className="period">/forever</span>
                </div>
                <p className="plan-description">{plans.free.description}</p>
              </div>
              <div className="card-body">
                <ul className="features-list">
                  {plans.free.features.map((feature, index) => (
                    <li key={index} className={`feature-item ${!feature.included ? 'disabled' : ''}`}>
                      {feature.included ? (
                        <Check className="w-5 h-5 text-green-500" />
                      ) : (
                        <X className="w-5 h-5 text-gray-500" />
                      )}
                      <span>{feature.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="card-footer">
                <button
                  onClick={() => navigate('/onboarding')}
                  className="btn btn-secondary btn-full"
                >
                  Get Started Free
                </button>
              </div>
            </motion.div>

            {/* Premium Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="pricing-card premium"
            >
              {plans.premium.badge && (
                <div className="plan-badge">
                  <Star className="w-4 h-4" />
                  {plans.premium.badge}
                </div>
              )}
              <div className="card-header">
                <h3 className="plan-name">
                  <Crown className="w-6 h-6 text-yellow-500" />
                  {plans.premium.name}
                </h3>
                <div className="plan-price">
                  <span className="price">
                    ${billingCycle === 'yearly' ? plans.premium.yearlyPrice : plans.premium.monthlyPrice}
                  </span>
                  <span className="period">
                    /{billingCycle === 'yearly' ? 'year' : 'month'}
                  </span>
                </div>
                {billingCycle === 'yearly' && (
                  <p className="monthly-equivalent">
                    Just ${(plans.premium.yearlyPrice / 12).toFixed(2)}/month
                  </p>
                )}
                <p className="plan-description">{plans.premium.description}</p>
              </div>
              <div className="card-body">
                <ul className="features-list">
                  {plans.premium.features.map((feature, index) => (
                    <li key={index} className={`feature-item ${feature.highlight ? 'highlight' : ''}`}>
                      <Check className="w-5 h-5 text-green-500" />
                      <span>{feature.name}</span>
                      {feature.highlight && <Zap className="w-4 h-4 text-yellow-500" />}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="card-footer">
                {isPremium() ? (
                  <button className="btn btn-success btn-full" disabled>
                    <Check className="w-5 h-5" />
                    Current Plan
                  </button>
                ) : (
                  <button
                    onClick={handleSubscribe}
                    className="btn btn-primary btn-full"
                    disabled={processing}
                  >
                    {processing ? (
                      'Processing...'
                    ) : (
                      <>
                        Upgrade to Premium
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </motion.div>
          </div>

          {/* Trust indicators */}
          <div className="trust-section">
            <p className="trust-text">
              <Check className="w-5 h-5 text-green-500" />
              7-day money-back guarantee
            </p>
            <p className="trust-text">
              <Check className="w-5 h-5 text-green-500" />
              Cancel anytime
            </p>
            <p className="trust-text">
              <Check className="w-5 h-5 text-green-500" />
              Secure payment via Stripe
            </p>
          </div>

          {/* FAQ Preview */}
          <div className="pricing-faq">
            <h2>Questions?</h2>
            <p>
              Check our <button onClick={() => navigate('/')} className="text-link">FAQ section</button> or
              email us at <a href="mailto:support@surviveitapp.com" className="text-link">support@surviveitapp.com</a>
            </p>
          </div>
        </div>
      </main>

      <Footer />

      <style jsx>{`
        .pricing-screen {
          position: relative;
          min-height: 100vh;
          background: radial-gradient(circle at 15% 20%, rgba(249, 115, 22, 0.08), transparent 32%),
                      radial-gradient(circle at 80% 0%, rgba(59, 130, 246, 0.1), transparent 35%),
                      linear-gradient(135deg, #0b1220 0%, #0f172a 50%, #0b1326 100%);
          color: white;
        }

        .pricing-screen::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(120deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px),
            linear-gradient(0deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
          background-size: 120px 120px;
          opacity: 0.25;
          pointer-events: none;
        }

        .nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 50;
          background: rgba(15, 23, 42, 0.9);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .nav-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem 0;
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
        }

        .pricing-main {
          position: relative;
          z-index: 1;
          padding: 8rem 0 4rem;
        }

        .nav {
          z-index: 50;
        }

        .pricing-hero {
          text-align: center;
          margin-bottom: 3rem;
        }

        .pricing-title {
          font-size: 3rem;
          font-weight: 800;
          margin-bottom: 1rem;
        }

        .text-gradient {
          background: linear-gradient(135deg, #f97316 0%, #dc2626 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .pricing-subtitle {
          font-size: 1.25rem;
          color: rgba(255, 255, 255, 0.7);
        }

        .billing-toggle {
          display: flex;
          justify-content: center;
          gap: 0.5rem;
          margin-bottom: 3rem;
          background: rgba(255, 255, 255, 0.1);
          padding: 0.5rem;
          border-radius: 0.75rem;
          width: fit-content;
          margin-left: auto;
          margin-right: auto;
        }

        .toggle-btn {
          padding: 0.75rem 1.5rem;
          border-radius: 0.5rem;
          border: none;
          background: transparent;
          color: rgba(255, 255, 255, 0.7);
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .toggle-btn.active {
          background: #f97316;
          color: white;
        }

        .discount-badge {
          font-size: 0.75rem;
          background: #22c55e;
          padding: 0.25rem 0.5rem;
          border-radius: 1rem;
        }

        .pricing-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 2rem;
          max-width: 800px;
          margin: 0 auto;
        }

        .pricing-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 1.5rem;
          overflow: hidden;
          position: relative;
        }

        .pricing-card.premium {
          border-color: #f97316;
          box-shadow: 0 0 40px rgba(249, 115, 22, 0.2);
        }

        .plan-badge {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: linear-gradient(135deg, #f97316 0%, #dc2626 100%);
          padding: 0.5rem 1rem;
          border-radius: 2rem;
          font-size: 0.75rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .card-header {
          padding: 2rem;
          text-align: center;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .plan-name {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .plan-price {
          margin-bottom: 0.5rem;
        }

        .price {
          font-size: 3rem;
          font-weight: 800;
        }

        .period {
          font-size: 1rem;
          color: rgba(255, 255, 255, 0.6);
        }

        .monthly-equivalent {
          font-size: 0.875rem;
          color: #22c55e;
          margin-bottom: 0.5rem;
        }

        .plan-description {
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.875rem;
        }

        .card-body {
          padding: 2rem;
        }

        .features-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 0.875rem;
        }

        .feature-item.disabled {
          color: rgba(255, 255, 255, 0.4);
        }

        .feature-item.highlight {
          color: #f97316;
          font-weight: 500;
        }

        .card-footer {
          padding: 0 2rem 2rem;
        }

        .btn-full {
          width: 100%;
          justify-content: center;
        }

        .btn-secondary {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .btn-success {
          background: #22c55e;
        }

        .trust-section {
          display: flex;
          justify-content: center;
          gap: 2rem;
          margin-top: 3rem;
          flex-wrap: wrap;
        }

        .trust-text {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.7);
        }

        .pricing-faq {
          text-align: center;
          margin-top: 4rem;
          padding: 2rem;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 1rem;
          max-width: 500px;
          margin-left: auto;
          margin-right: auto;
        }

        .pricing-faq h2 {
          font-size: 1.25rem;
          margin-bottom: 0.5rem;
        }

        .pricing-faq p {
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.875rem;
        }

        .text-link {
          color: #f97316;
          background: none;
          border: none;
          cursor: pointer;
          text-decoration: underline;
        }

        .btn-ghost {
          background: transparent;
          color: rgba(255, 255, 255, 0.9);
          border: 1px solid rgba(255, 255, 255, 0.2);
          padding: 0.5rem 1rem;
          border-radius: 0.5rem;
          cursor: pointer;
        }

        @media (max-width: 768px) {
          .pricing-title {
            font-size: 2rem;
          }

          .pricing-grid {
            grid-template-columns: 1fr;
          }

          .trust-section {
            flex-direction: column;
            align-items: center;
            gap: 1rem;
          }
        }
      `}</style>
    </div>
  )
}

export default PricingScreen
